import React from 'react';
import { Content } from '../useContentData';
import { getContentType } from './registry';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface ContentEditorProps {
  content: Content;
  onSaved?: () => void;
  onCancel?: () => void;
  onUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
}

/**
 * Content editor that selects the appropriate editor component based on content type
 */
export const ContentEditor: React.FC<ContentEditorProps> = ({ 
  content,
  onSaved,
  onCancel,
  onUnsavedChanges
}) => {
  const { toast } = useToast();
  
  // Handle saving content
  const handleSave = async (updatedContent: Partial<Content>, exitEditMode: boolean = true) => {
    try {
      // Use the create_content_version RPC to create a new version
      const { error } = await supabase.rpc('create_content_version', {
        p_content_id: content.id,
        p_content_json: updatedContent.content_json,
        p_name: updatedContent.name || content.name,
        p_change_summary: 'Edited via UI',
        p_status: 'READY',
        p_metadata: {}, // Add metadata parameter to resolve function overloading
      });
      
      if (error) throw error;
      
      toast({
        title: 'Content saved',
        description: 'Your changes have been saved as a new version.',
      });
      
      // Call onSaved callback if provided and exitEditMode is true
      if (exitEditMode && onSaved) onSaved();
    } catch (error: any) {
      console.error('Error saving content:', error);
      toast({
        title: 'Error saving content',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    if (onCancel) onCancel();
  };
  
  // Guard against null content
  if (!content) {
    return <div>No content available</div>;
  }

  // Get the editor for this content type from the registry
  const contentType = getContentType(content.content_type);
  
  // If we have a registered editor, use it, otherwise show an error
  if (contentType && contentType.editor) {
    const EditorComponent = contentType.editor;
    return (
      <EditorComponent 
        content={content} 
        onSave={handleSave} 
        onCancel={handleCancel}
        onUnsavedChanges={onUnsavedChanges}
      />
    );
  }
  
  // No editor available for this content type
  return (
    <div className="w-full h-full overflow-auto p-6">
      <Alert className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No Editor Available</AlertTitle>
        <AlertDescription>
          No editor is available for the content type "{content.content_type}".
          Please implement an editor for this content type.
        </AlertDescription>
      </Alert>
      
      <pre className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-md overflow-auto">
        {JSON.stringify(content, null, 2)}
      </pre>
    </div>
  );
};

export default ContentEditor; 